import { useState, useEffect } from 'react';

/**
 * Local clock interpolated between server updates.
 *
 * Resets to `serverTime` whenever it changes, then ticks once a second by the
 * time elapsed since that update. Does not tick until a non-zero server time
 * has arrived.
 *
 * @param {number|undefined} serverTime - clock_time from the server, in seconds
 * @returns {number} the interpolated clock time, in seconds
 */
export const useClock = (serverTime) => {
  const [clockTime, setClockTime] = useState(0);
  const [lastServerTime, setLastServerTime] = useState(0);
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());

  // Update clock time when the server time changes
  useEffect(() => {
    if (serverTime !== undefined) {
      setLastServerTime(serverTime);
      setLastUpdateTime(Date.now());
      setClockTime(serverTime);
    }
  }, [serverTime]);

  // Timer effect to update the clock
  useEffect(() => {
    if (lastServerTime === 0) return; // Don't start timer until we have server time

    const timer = setInterval(() => {
      // Calculate elapsed time since last server update (in seconds)
      const elapsedSinceUpdate = (Date.now() - lastUpdateTime) / 1000;
      // Update clock time based on last server time plus elapsed time
      setClockTime(lastServerTime + elapsedSinceUpdate);
    }, 1000);

    return () => clearInterval(timer);
  }, [lastServerTime, lastUpdateTime]);

  return clockTime;
};

export default useClock;
